<div class="rolemanager-container">

  <!-- ── ERROR BANNER ──────────────────────────────────────────── -->
  <div class="error-banner" *ngIf="errorMsg">
    <span>⚠ {{ errorMsg }}</span>
    <button (click)="loadAllUsers()">Retry</button>
  </div>

  <!-- ── STAT CARDS ─────────────────────────────────────────────── -->
  <div class="stats-overview-grid">
    <div class="overview-box box-primary">
      <div class="box-number">{{ totalRoles }}</div>
      <div class="box-title">Total Roles</div>
    </div>
    <div class="overview-box box-success">
      <div class="box-number">{{ activeRoles }}</div>
      <div class="box-title">Roles With Active Users</div>
    </div>
    <div class="overview-box box-info">
      <div class="box-number">{{ totalUsers }}</div>
      <div class="box-title">Total Users</div>
    </div>
    <div class="overview-box box-warning">
      <div class="box-number">{{ assignedUsers }}</div>
      <div class="box-title">Active Users</div>
    </div>
  </div>

  <!-- ── LOADING ────────────────────────────────────────────────── -->
  <div class="loading-bar-wrap" *ngIf="isLoading">
    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
    <p class="loading-text">Loading user data from server…</p>
  </div>

  <div class="content-layout" *ngIf="!isLoading">

    <!-- LEFT PANEL -->
    <div class="roles-panel">
      <div class="panel-header">
        <h2 class="header-title">System Roles</h2>
        <span class="roles-note">Roles are defined by the system</span>
      </div>

      <div class="controls-section">
        <input type="text" class="search-input"
               placeholder="Search roles…" [(ngModel)]="searchTerm"/>
        <select class="filter-dropdown" [(ngModel)]="filterStatus">
          <option value="All">All Roles</option>
          <option value="Has Users">Has Users</option>
          <option value="No Users">No Users</option>
        </select>
      </div>

      <div class="roles-listing">
        <div class="role-item"
             *ngFor="let role of getFilteredRoles()"
             [class.item-selected]="selectedRole?.key === role.key"
             (click)="selectRole(role)">

          <div class="role-color-dot" [style.background]="getRoleDotColor(role.key)"></div>

          <div class="role-info">
            <div class="role-name-row">
              <h3 class="role-name">{{ role.label }}</h3>
              <code class="role-key">{{ role.key }}</code>
            </div>
            <p class="role-description">{{ role.description }}</p>
            <div class="role-meta">
              <span class="meta-tag meta-users"><strong>{{ role.userCount }}</strong> users</span>
              <span class="meta-tag meta-active" *ngIf="role.activeCount > 0">
                <strong>{{ role.activeCount }}</strong> active
              </span>
              <span class="meta-tag"><strong>{{ role.permissions.length }}</strong> module access</span>
            </div>
          </div>

          <div class="role-status-indicator"
               [class.indicator-active]="role.userCount > 0"
               [class.indicator-inactive]="role.userCount === 0">
            {{ role.userCount > 0 ? 'In Use' : 'Unused' }}
          </div>
        </div>

        <div class="empty-state" *ngIf="getFilteredRoles().length === 0">
          <p>No roles match your search</p>
        </div>
      </div>
    </div>

    <!-- RIGHT PANEL — selected role -->
    <div class="details-panel" *ngIf="selectedRole">
      <div class="details-header">
        <div>
          <h2 class="details-title">{{ selectedRole.label }}</h2>
          <p class="details-subtitle">{{ selectedRole.description }}</p>
        </div>
        <button class="btn-close" (click)="clearSelection()">✕</button>
      </div>

      <div class="details-body">

        <!-- Overview grid -->
        <div class="info-section">
          <h3 class="section-label">Role Overview</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-key">Role Key</span>
              <span class="info-value"><code class="role-key">{{ selectedRole.key }}</code></span>
            </div>
            <div class="info-item">
              <span class="info-key">Total Users</span>
              <span class="info-value">{{ selectedRole.userCount }}</span>
            </div>
            <div class="info-item">
              <span class="info-key">Active Users</span>
              <span class="info-value">{{ selectedRole.activeCount }}</span>
            </div>
            <div class="info-item">
              <span class="info-key">Module Access</span>
              <span class="info-value">{{ selectedRole.permissions.length }} modules</span>
            </div>
          </div>
        </div>

        <!-- Module access -->
        <div class="permissions-section">
          <h3 class="section-label">Module Access</h3>
          <div class="permissions-list">
            <div class="permission-card card-enabled" *ngFor="let perm of selectedRole.permissions">
              <div class="permission-header">
                <span class="permission-name">{{ perm }}</span>
                <span class="permission-toggle toggle-on">Enabled</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Users in this role -->
        <div class="users-section">
          <h3 class="section-label">
            Users in this Role
            <span class="users-count-badge">{{ selectedRole.users.length }}</span>
          </h3>

          <div class="role-users-loading" *ngIf="roleUsersLoading">
            <mat-progress-bar mode="indeterminate"></mat-progress-bar>
            <p>Fetching users…</p>
          </div>

          <div class="users-list" *ngIf="!roleUsersLoading">
            <div class="user-row" *ngFor="let user of selectedRole.users">

              <!-- Avatar -->
              <div class="user-avatar" [style.background]="getRoleDotColor(user.adminType)">
                {{ getUserInitials(user) }}
              </div>

              <!-- Info -->
              <div class="user-info">
                <span class="user-name">{{ getFullName(user) }}</span>
                <span class="user-username">{{ user.username }}</span>
                <span class="user-email">{{ user.email }}</span>
              </div>

              <!-- Status -->
              <span class="user-status-badge" [class]="'us-' + getUserStatusClass(user)">
                <span class="udot"></span>{{ getUserStatusLabel(user) }}
              </span>

              <!-- Action buttons -->
              <div class="user-actions">

                <!-- ACTIVATE — inactive & not terminated -->
                <button mat-icon-button matTooltip="Activate account"
                        class="ua-btn ua-activate"
                        *ngIf="!user.active && user.employeeStatus !== 'TERMINATED'"
                        (click)="openActivateDialog(user, $event)">
                  <mat-icon>check_circle_outline</mat-icon>
                </button>

                <!-- DEACTIVATE — active & not terminated -->
                <button mat-icon-button matTooltip="Deactivate account"
                        class="ua-btn ua-deactivate"
                        *ngIf="user.active && user.employeeStatus !== 'TERMINATED'"
                        (click)="openDeactivateDialog(user, $event)">
                  <mat-icon>block</mat-icon>
                </button>

                <!-- TERMINATE — not already terminated -->
                <button mat-icon-button matTooltip="Terminate account"
                        class="ua-btn ua-terminate"
                        *ngIf="user.employeeStatus !== 'TERMINATED'"
                        (click)="openTerminateDialog(user, $event)">
                  <mat-icon>person_off</mat-icon>
                </button>

                <!-- TERMINATED — no actions available -->
                <span class="ua-terminated-tag" *ngIf="user.employeeStatus === 'TERMINATED'">
                  <mat-icon>do_not_disturb_on</mat-icon> Terminated
                </span>

              </div>
            </div><!-- /user-row -->

            <div class="empty-users" *ngIf="selectedRole.users.length === 0">
              <p>No users are assigned to this role</p>
            </div>
          </div>
        </div>

      </div><!-- /details-body -->
    </div><!-- /details-panel -->

    <!-- RIGHT PANEL — empty state -->
    <div class="details-panel details-empty" *ngIf="!selectedRole">
      <div class="empty-content">
        <div class="empty-icon">🔐</div>
        <h3>No Role Selected</h3>
        <p>Click a role from the list to view its details, module access and assigned users</p>
      </div>
    </div>

  </div><!-- /content-layout -->
</div><!-- /rolemanager-container -->


<!-- ═══════════════════════════════════════════════════════
     ACTION DIALOG  (activate / deactivate / terminate)
════════════════════════════════════════════════════════ -->
<ng-template #actionDialogTpl>
  <div class="umd-wrap">

    <div class="umd-header" [class]="'umd-' + dialogMode">
      <mat-icon class="umd-hicon">{{ dialogIcon }}</mat-icon>
      <div>
        <h2 class="umd-title">{{ dialogTitle }}</h2>
        <p class="umd-sub" *ngIf="selectedUser">
          {{ selectedUser.firstName }} {{ selectedUser.lastName }}
          &bull; {{ selectedUser.username }}
        </p>
      </div>
    </div>

    <mat-dialog-content class="umd-body">

      <!-- User summary card -->
      <div class="umd-user-card" *ngIf="selectedUser as u">
        <div class="umd-av" [style.background]="getRoleDotColor(u.adminType)">
          {{ getUserInitials(u) }}
        </div>
        <div class="umd-uinfo">
          <span class="umd-uname">{{ getFullName(u) }}</span>
          <code class="role-key">{{ u.username }}</code>
          <span class="umd-uemail">{{ u.email }}</span>
        </div>
        <span class="user-status-badge" [class]="'us-' + getUserStatusClass(u)">
          <span class="udot"></span>{{ getUserStatusLabel(u) }}
        </span>
      </div>

      <!-- Contextual notice -->
      <div class="umd-notice" [class.umd-notice-danger]="dialogMode === 'terminate'">
        <mat-icon>info</mat-icon>
        <span [ngSwitch]="dialogMode">
          <ng-container *ngSwitchCase="'activate'">
            The user will immediately regain access to all their permitted modules.
          </ng-container>
          <ng-container *ngSwitchCase="'deactivate'">
            The user's login will be suspended until a SYSADMIN reactivates the account.
          </ng-container>
          <ng-container *ngSwitchCase="'terminate'">
            <strong>This action is permanent.</strong>
            The user will be fully removed from active service with no portal access.
          </ng-container>
        </span>
      </div>

      <!-- Reason field — deactivate & terminate only -->
      <form [formGroup]="actionForm" *ngIf="dialogMode !== 'activate'">
        <mat-form-field appearance="outline" class="umd-reason">
          <mat-label>
            {{ dialogMode === 'terminate' ? 'Termination reason' : 'Reason for deactivation' }}
          </mat-label>
          <textarea matInput formControlName="reason" rows="3"
                    placeholder="Provide a clear, documented reason…"></textarea>
          <mat-hint align="end">{{ actionForm.get('reason')?.value?.length || 0 }} chars</mat-hint>
          <mat-error *ngIf="actionForm.get('reason')?.hasError('required')">Required</mat-error>
          <mat-error *ngIf="actionForm.get('reason')?.hasError('minlength')">Min 5 characters</mat-error>
        </mat-form-field>
      </form>

    </mat-dialog-content>

    <mat-dialog-actions class="umd-footer">
      <button mat-stroked-button (click)="closeDialog()" [disabled]="actionInProgress">
        Cancel
      </button>
      <button mat-raised-button
              class="umd-confirm" [class]="'umd-confirm-' + dialogMode"
              (click)="confirmAction()"
              [disabled]="actionInProgress || (dialogMode !== 'activate' && actionForm.invalid)">
        <mat-spinner *ngIf="actionInProgress" diameter="16" color="accent"></mat-spinner>
        <mat-icon *ngIf="!actionInProgress">{{ dialogIcon }}</mat-icon>
        <span>{{ actionInProgress ? 'Processing…' : 'Confirm ' + (dialogMode | titlecase) }}</span>
      </button>
    </mat-dialog-actions>

  </div>
</ng-template>